import { useIsMutating } from '@tanstack/react-query';
import Button from 'components/button/Button';
import PropertyMultiSelectFilterField from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField';
import FormWithProvider from 'components/forms/form/Form';
import useAppModal from 'hooks/useAppModal';
import { AppliesToLabel } from 'pages/property/common/Common.styles';
import { Col, Modal, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import {
  APPLIES_TO_FIELD_NAME,
  deleteSeasonalRuleModalId,
} from './DeleteSeasonalRuleModal.constants';
import {
  deleteSeasonalRuleFormSchema,
  DeleteSeasonalRuleFormValues,
} from './DeleteSeasonalRuleModal.schema';

interface Props {
  ruleName: string;
  onSubmit: (values: DeleteSeasonalRuleFormValues) => Promise<void>;
  defaultValues: DeleteSeasonalRuleFormValues;
  mutationKey: (string | number)[];
}

const DeleteSeasonalRuleModal = ({
  ruleName,
  onSubmit,
  defaultValues,
  mutationKey,
}: Props) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const isMutating = useIsMutating({ mutationKey });
  const isLoading = isMutating > 0;

  const handleClose = () => {
    closeModal(deleteSeasonalRuleModalId);
  };

  return (
    <FormWithProvider
      horizontal
      resolver={deleteSeasonalRuleFormSchema()}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('pageProperty.seasonality.deleteModal.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12} data-testid="delete-seasonal-rule-description">
            <Trans
              i18nKey="pageProperty.seasonality.deleteModal.description"
              values={{ name: ruleName }}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={3}>
            <AppliesToLabel>
              {t('pageProperty.common.cells.appliesTo')}
            </AppliesToLabel>
          </Col>
          <Col xs={9}>
            <fieldset disabled={isLoading}>
              <PropertyMultiSelectFilterField
                name={APPLIES_TO_FIELD_NAME}
                enableHotels={false}
                enableUnitTypes
              />
            </fieldset>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          bsStyle="default"
          data-testid="delete-seasonal-rule-cancel-button"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          bsStyle="danger"
          data-testid="delete-seasonal-rule-submit-button"
          type="submit"
          disabled={isLoading}
        >
          {t('pageProperty.seasonality.deleteModal.deleteButton')}
        </Button>
      </Modal.Footer>
    </FormWithProvider>
  );
};

export default DeleteSeasonalRuleModal;
